/**
 * The cryptography behind the offline copy.
 *
 * A tenant's ledger must not sit in readable form on a laptop or a phone, so nothing reaches
 * the device store in the clear. One random data key encrypts every cached record and every
 * queued write; that data key is itself stored only in wrapped form.
 *
 * There are two wrappings of the same data key, and the difference between them is the whole
 * security model:
 *
 *   - The **password wrapping** is PBKDF2 over the operator's own password. It is what makes
 *     signing in offline mean something: with no server to ask, the password is the only thing
 *     that can open the copy, and a wrong password fails as an authentication-tag mismatch
 *     rather than as a comparison this code could be talked out of.
 *   - The **device wrapping** uses a key that is generated non-extractably and kept in
 *     IndexedDB, so script can use it but never read it. It exists only so that a reload of an
 *     already-authenticated session can keep the offline copy current without asking for the
 *     password again, and it is removed on sign-out. It grants no more than the session cookie
 *     sitting beside it in the same browser profile already grants.
 */

/** OWASP's 2023 floor for PBKDF2-HMAC-SHA256. Recorded per vault so it can be raised later. */
export const PBKDF2_ITERATIONS = 600_000;

const KEY_LENGTH_BITS = 256;
const IV_LENGTH = 12;
const SALT_LENGTH = 16;

export class OfflineCryptoUnavailableError extends Error {
  constructor() {
    super('This browser cannot encrypt the offline copy: Web Crypto is unavailable.');
    this.name = 'OfflineCryptoUnavailableError';
  }
}

/**
 * True when the offline copy can be protected at all.
 *
 * `crypto.subtle` is only exposed in a secure context, so an origin served over plain HTTP
 * gets `false` here and the offline features stay switched off rather than storing a tenant's
 * figures unencrypted.
 */
export function offlineCryptoAvailable(): boolean {
  return typeof globalThis.crypto?.subtle?.importKey === 'function';
}

function subtle(): SubtleCrypto {
  if (!offlineCryptoAvailable()) throw new OfflineCryptoUnavailableError();
  return globalThis.crypto.subtle;
}

export function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  globalThis.crypto.getRandomValues(bytes);
  return bytes;
}

export function toBase64(bytes: Uint8Array): string {
  let text = '';
  for (const byte of bytes) text += String.fromCharCode(byte);
  return globalThis.btoa(text);
}

export function fromBase64(value: string): Uint8Array {
  const text = globalThis.atob(value);
  const bytes = new Uint8Array(text.length);
  for (let index = 0; index < text.length; index += 1) bytes[index] = text.charCodeAt(index);
  return bytes;
}

/** A fresh PBKDF2 salt, one per enrolled device. */
export function createSalt(): string {
  return toBase64(randomBytes(SALT_LENGTH));
}

/** Stretches a password into the key that wraps the data key. */
export async function deriveWrappingKey(password: string, salt: string, iterations: number): Promise<CryptoKey> {
  const material = await subtle().importKey('raw', new TextEncoder().encode(password), 'PBKDF2', false, ['deriveKey']);
  return subtle().deriveKey(
    { name: 'PBKDF2', salt: fromBase64(salt) as unknown as BufferSource, iterations, hash: 'SHA-256' },
    material,
    { name: 'AES-GCM', length: KEY_LENGTH_BITS },
    false,
    ['encrypt', 'decrypt'],
  );
}

/** The data key: extractable, because it has to be wrapped, and never stored unwrapped. */
export function createDataKey(): Promise<CryptoKey> {
  return subtle().generateKey({ name: 'AES-GCM', length: KEY_LENGTH_BITS }, true, ['encrypt', 'decrypt']);
}

/**
 * The device key: generated non-extractably so that even this code cannot read its bytes. It
 * is handed to IndexedDB as a `CryptoKey` object rather than as key material.
 */
export function createDeviceKey(): Promise<CryptoKey> {
  return subtle().generateKey({ name: 'AES-GCM', length: KEY_LENGTH_BITS }, false, ['encrypt', 'decrypt']);
}

/** Encrypts bytes under a key. The random IV is carried in front of the ciphertext. */
export async function seal(key: CryptoKey, plaintext: Uint8Array): Promise<string> {
  const iv = randomBytes(IV_LENGTH);
  const cipher = await subtle().encrypt(
    { name: 'AES-GCM', iv: iv as unknown as BufferSource },
    key,
    plaintext as unknown as BufferSource,
  );
  const payload = new Uint8Array(iv.length + cipher.byteLength);
  payload.set(iv, 0);
  payload.set(new Uint8Array(cipher), iv.length);
  return toBase64(payload);
}

/** Reverses `seal`. Throws when the key is wrong or the payload has been altered. */
export async function open(key: CryptoKey, payload: string): Promise<Uint8Array> {
  const bytes = fromBase64(payload);
  const iv = bytes.subarray(0, IV_LENGTH);
  const cipher = bytes.subarray(IV_LENGTH);
  const plaintext = await subtle().decrypt(
    { name: 'AES-GCM', iv: iv as unknown as BufferSource },
    key,
    cipher as unknown as BufferSource,
  );
  return new Uint8Array(plaintext);
}

export async function sealJson(key: CryptoKey, value: unknown): Promise<string> {
  return seal(key, new TextEncoder().encode(JSON.stringify(value)));
}

export async function openJson<T>(key: CryptoKey, payload: string): Promise<T> {
  return JSON.parse(new TextDecoder().decode(await open(key, payload))) as T;
}

/** Wraps the data key so it can be written to the device store. */
export async function wrapDataKey(wrappingKey: CryptoKey, dataKey: CryptoKey): Promise<string> {
  const raw = new Uint8Array(await subtle().exportKey('raw', dataKey));
  return seal(wrappingKey, raw);
}

/**
 * Unwraps the data key. A wrong password reaches here and fails, because AES-GCM refuses to
 * decrypt a payload whose authentication tag does not match.
 */
export async function unwrapDataKey(wrappingKey: CryptoKey, wrapped: string): Promise<CryptoKey> {
  const raw = await open(wrappingKey, wrapped);
  return subtle().importKey('raw', raw as unknown as BufferSource, { name: 'AES-GCM' }, true, ['encrypt', 'decrypt']);
}
